/**
 * `useSchoolTopUpWatch` — the school-checkout twin of `useTopUpWatch`.
 *
 * Same problem, different query: a Stripe top-up of the school's
 * ORGANIZATION account credits the ledger only when the
 * `payment_intent.succeeded` webhook lands, so after the Payment Element
 * confirms, the checkout polls `GET /api/billing-school/account` on the
 * shared bounded backoff (`buildTopUpPollDelays`) until the new balance
 * appears — the insufficient-funds gate then lifts reactively and the
 * Submit button returns in place ("take them back to what they were
 * doing"). The schedule + landed-check are the same pure helpers the
 * family watcher uses; only the cache read differs (one flat
 * `SchoolBillingAccount` payload instead of the family accounts array).
 */

import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'

import { type SchoolBillingAccount, schoolBillingAccountQueryKey } from '@/api/billing-school-account'
import { buildTopUpPollDelays, topUpHasLanded } from '@/lib/top-up-polling'

export interface SchoolTopUpWatchState {
  /** True while we're waiting on a Stripe credit to land. */
  pending: boolean
  /**
   * True once the poll window (~60s) elapsed without the credit landing —
   * show a gentle "still processing" note instead of a live spinner.
   */
  stillProcessing: boolean
}

export interface UseSchoolTopUpWatchResult extends SchoolTopUpWatchState {
  /** Begin watching the school's account for an `amountCents` credit. */
  watch: (args: { amountCents: number }) => void
}

export function useSchoolTopUpWatch(schoolLocationId: string): UseSchoolTopUpWatchResult {
  const qc = useQueryClient()
  const [state, setState] = useState<SchoolTopUpWatchState>({ pending: false, stillProcessing: false })
  // Bumped on every new watch (and on unmount) so a superseded or orphaned
  // poll loop cancels itself instead of racing the current one.
  const runRef = useRef(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const cancelActiveWatch = useCallback(() => {
    runRef.current++
    clearTimer()
  }, [clearTimer])

  const readBalance = useCallback(
    (): number | null =>
      qc.getQueryData<SchoolBillingAccount>(schoolBillingAccountQueryKey(schoolLocationId))?.balanceCents ?? null,
    [qc, schoolLocationId],
  )

  const watch = useCallback(
    ({ amountCents }: { amountCents: number }) => {
      clearTimer()
      const run = ++runRef.current
      // Baseline off the balance the gate is already showing; the credit has
      // landed once we observe at least baseline + this top-up.
      const target = (readBalance() ?? 0) + amountCents
      const delays = buildTopUpPollDelays()
      setState({ pending: true, stillProcessing: false })

      let index = 0
      const tick = async () => {
        if (run !== runRef.current) {
          return
        }
        await qc.refetchQueries({ queryKey: schoolBillingAccountQueryKey(schoolLocationId) })
        if (run !== runRef.current) {
          return
        }
        const balance = readBalance()
        if (balance !== null && topUpHasLanded(balance, target)) {
          setState({ pending: false, stillProcessing: false })
          return
        }
        if (index >= delays.length) {
          // Budget exhausted — stop polling and leave a gentle note. The gate
          // still lifts on the next refetch/invalidation once the credit lands.
          setState({ pending: false, stillProcessing: true })
          return
        }
        timerRef.current = setTimeout(() => void tick(), delays[index++])
      }

      timerRef.current = setTimeout(() => void tick(), delays[index++])
    },
    [clearTimer, qc, readBalance, schoolLocationId],
  )

  useEffect(() => {
    return cancelActiveWatch
  }, [cancelActiveWatch])

  return { ...state, watch }
}
